import {
    HttpClient,
    HttpHeaders,
    HttpParams,
    HttpResponse,
} from "@angular/common/http";
import { Injectable } from "@angular/core";
import { environment } from "../../../environments/environment";
import { Token } from "../../app.model";
import { CacheService } from "../../cache.service";
import { NS_Enums,NS_SubscriptionService } from "../../shared/namespaces";
import {
    CompanySubscription,
    CompanySubscriptionAddonResponse,
    UpdateBillingAddress
} from "../models/subscription-models";
import {
    filter as _filter,
    find,
    get,
    includes,
    isNil,
    isUndefined,
    lowerCase,
} from "lodash-es";
import { BehaviorSubject, Observable, of } from "rxjs";
import { DataService } from "src/app/shared/services/data/data.service";
import { RequestParam } from "src/app/connector-studio/interfaces";
import { AppService } from "src/app/app.service";
import { map, filter, tap } from "rxjs/operators";
import { CryptoService } from "src/app/shared/services/crypto/crypto.service";

@Injectable({
    providedIn: "root",
})
export class SubscriptionDataService {
    constructor(dataService: DataService, private appService: AppService, private http: HttpClient, private cacheService: CacheService, private cryptoService: CryptoService) {
        this.dataService = dataService;
     }
    public dataService: DataService;
    private apiURL = environment.subscriptionApiUrl;
    private datonAPIUrl = environment.apiUrl;
    private subscriptionServiceDataMap = new Map<NS_SubscriptionService.subscriptionServiceDataKeyEnum , any>();
    private _productName: string = '';
    private companySubscriptionByCompanyId$ = new BehaviorSubject<any>(null);
    private companySubscriptionLoadingState = new Map<string, boolean>();
    set productName(productName){
      this._productName = productName;
    }
    
    clearSubscriptionServiceMapData(){
        this.subscriptionServiceDataMap.clear();
        this.clearCompanySubscriptionCache();
    }
    
    clearCompanySubscriptionCache(){
        this.companySubscriptionByCompanyId$.next(null);
        this.companySubscriptionLoadingState.clear();
    }
    
    getCompanySubscriptionByCompanyIdObservable(): Observable<any> {
        return this.companySubscriptionByCompanyId$.asObservable();
    }
    subscriptionPlan = {
        getTrialSubscriptionPlan: (productId:number = 1) => {
            return new Observable((observer) => {
                const option = {
                    url: `${this.apiURL}/subscription-plan?productId=${productId}&status=${NS_SubscriptionService.SubscriptionPlanStatusEnum.ACTIVE}&size=200`
                }
                this.dataService.get(option).subscribe((response:any) => {
                    let subscriptionPlans = get(response, ['data','content'],[]);
                    let subscriptionPlan = find(subscriptionPlans, (plan)=> {
                        return plan.name == NS_SubscriptionService.trialSubscriptionPlanName;
                    });
                    observer.next(subscriptionPlan);
                    observer.complete();
                });
            });
        },
        getSubscriptionPlans: (productId:number) => {
            return new Observable((observer) => {
                let subscriptionPlans = this.subscriptionServiceDataMap.get(NS_SubscriptionService.subscriptionServiceDataKeyEnum.subscriptionPlans);
                if(!isUndefined(subscriptionPlans) && subscriptionPlans.length > 0){
                    observer.next(subscriptionPlans);
                    observer.complete();
                } else {
                    const option = {
                        url: `${this.apiURL}/subscription-plan?productId=${productId}&status=${NS_SubscriptionService.SubscriptionPlanStatusEnum.ACTIVE}&size=200`
                    }
                    this.dataService.get(option).subscribe((response:any) => {
                        let subscriptionPlans = get(response, ['data','content'],[]);
                        // we dont need this filter because filtering will be done on pricing page
                        // subscriptionPlans = filter(subscriptionPlans, (plan)=> includes(NS_SubscriptionService.allowedSubscriptionPlanCategories, plan.subscriptionPlanCategory ));
                        this.subscriptionServiceDataMap.set(NS_SubscriptionService.subscriptionServiceDataKeyEnum.subscriptionPlans, subscriptionPlans);
                        observer.next(subscriptionPlans);
                        observer.complete();
                    });
                }
            });
        },
        getSubscriptionPlanDetails:(subscriptionPlanId) => {
            return new Observable((observer) => {
                if(!this.subscriptionServiceDataMap.has(NS_SubscriptionService.subscriptionServiceDataKeyEnum.subscriptionPlanDetails)){
                    this.subscriptionServiceDataMap.set(NS_SubscriptionService.subscriptionServiceDataKeyEnum.subscriptionPlanDetails, new Map());
                }
                let subscriptionPlanDetailsMap = this.subscriptionServiceDataMap.get(NS_SubscriptionService.subscriptionServiceDataKeyEnum.subscriptionPlanDetails);
                if( subscriptionPlanDetailsMap.has(subscriptionPlanId)){
                    observer.next(subscriptionPlanDetailsMap.get(subscriptionPlanId));
                    observer.complete();
                } else {
                    const option = {
                        url: `${this.apiURL}/subscription-plans/details?id=${subscriptionPlanId}`
                    }
                    this.dataService.get(option).subscribe((response:any) => {
                        let subscriptionPlanDetails = get(response, ['content', 0]);
                        subscriptionPlanDetailsMap.set(subscriptionPlanId, subscriptionPlanDetails);
                        this.subscriptionServiceDataMap.set(NS_SubscriptionService.subscriptionServiceDataKeyEnum.subscriptionPlanDetails, subscriptionPlanDetailsMap);
                        observer.next(subscriptionPlanDetails);
                        observer.complete();
                    });
                }
            });
        }
    }

    services = {
      // 'https://testwebapi.sarasanalytics.com/services/migrateSubscriptions'
      'migrateSubscriptions' : () => {
        const option = {
            url: `${this.datonAPIUrl}services/migrateSubscriptions`,
            header: {
              'Authorization': 'Basic ' + this.cryptoService.base64Encode(`${this.appService.userData.email}:${this.appService.userData.company.name}`),
              'skip': 'true'
            },
            data: {"companyIds":[this.appService.userData.company.companyId]}
        }
        return this.dataService.post(option)
      }
    }

    companySubscriptionAddon = {
        /**
         * Checks whether the company subscription has an active add-on
         * with the given name.
         * @param companySubscriptionId Active company subscription id.
         * @param addonName Add-on name to look up, for example
         * `DATON_CONNECT_TEMPLATE`.
         * @returns Observable that emits true when the add-on is present.
         */
        hasActiveAddon: (
            companySubscriptionId: number,
            addonName: NS_SubscriptionService.companySubscriptionAddonNameEnum
        ): Observable<boolean> => {
            const endpoint = '/company-subscription-addon/subscription';
            const url = `${this.apiURL}${endpoint}/${companySubscriptionId}/active`;
            return this.dataService.get({ url }).pipe(
                map(response => {
                    const addonResponse =
                        response as CompanySubscriptionAddonResponse;
                    return addonResponse.data?.some(
                        addonRecord =>
                            addonRecord.addon?.name === addonName
                    ) ?? false;
                })
            );
        }
    }

    companySubscription = {
        'checkUpgradeDowngradeById': (currentSubscriptionPlanId, targetSubscriptionPlanId, subscriptionStartMillis )=>{
            const option = {
                url: `${this.apiURL}/company-subscription-plan/check-upgrade-downgrade/by-id?currentPlanId=${currentSubscriptionPlanId}&targetPlanId=${targetSubscriptionPlanId}&subscriptionStartMillis=${subscriptionStartMillis}`
            }
            return this.dataService.get(option)
        },
        'downgrade': (companySubscriptionPlanId, newSubscriptionPlanId )=>{
            const option = {
                url: `${this.apiURL}/company-subscription-plan/${companySubscriptionPlanId}/downgrade?newSubscriptionPlanId=${newSubscriptionPlanId}`
            }
            return this.dataService.put(option)
        },
        'upgrade': (existingCompanySubscriptionId, newSubscriptionPlanId, forceTermReset )=>{
            const option = {
                url: `${this.apiURL}/company-subscription-plan/upgrade?existingCompanySubscriptionId=${existingCompanySubscriptionId}&targetSubscriptionPlanId=${newSubscriptionPlanId}&forceTermReset=${forceTermReset}`
            }
            return this.dataService.put(option)
        },
        'cancel': (companySubscriptionPlanId )=>{
            const option = {
                url: `${this.apiURL}/company-subscription-plan/${companySubscriptionPlanId}/cancel`
            }
            return this.dataService.put(option)
        },
        'saveCompanySubscription': (data : CompanySubscription) => {
            const option = {
                url: `${this.apiURL}/company-subscription/save`,
                data: data
            }
            return this.dataService.post(option)
        },
        'updateCompanySubscription': (companySubscriptionId, data : CompanySubscription) => {
            const option = {
                url: `${this.apiURL}/company-subscription/update/${companySubscriptionId}`,
                data: data
            }
            return this.dataService.put(option)
        },
        'startTrial':(trialCompanySubscriptionId)=>{
            const option = {
                url: `${this.apiURL}/company-subscription/start-trial/${trialCompanySubscriptionId}`
            }
            return this.dataService.put(option);
        },
        'getCompanySubscriptions': ()=> {
            const option = {
                url: `${this.apiURL}/company-subscription`
            }
            return this.dataService.get(option)
            
        },
        'getCompanySubscriptionById': (companySubscriptionID) => {
            const option = {
                url: `${this.apiURL}/company-subscription?id=${companySubscriptionID}`
            }
            return this.dataService.get(option)
        },
        'getCompanySubscriptionByCompanyId': (companyId) => {
            const cacheKey = `companyId_${companyId}`;
            const currentValue = this.companySubscriptionByCompanyId$.getValue();
            const isLoading = this.companySubscriptionLoadingState.get(cacheKey);
            
            // If data exists in cache, return it immediately
            if (currentValue && currentValue.companyId === companyId) {
                return of(currentValue.data);
            }
            
            // If already loading, return the observable to share the same request
            if (isLoading) {
                return this.companySubscriptionByCompanyId$.pipe(
                    filter((value) => value && value.companyId === companyId),
                    map((value) => value.data)
                );
            }
            
            // Mark as loading and fetch from API
            console.log('Company Subscription Fetching from API');
            this.companySubscriptionLoadingState.set(cacheKey, true);
            
            const option = {
                url: `${this.apiURL}/company-subscription?companyId=${companyId}`
            }
            
            return this.dataService.get(option).pipe(
                map((response) => {
                    return _filter(get(response, 'data.content'), (companySubscription: any) => {
                        return lowerCase(get(companySubscription, 'subscriptionPlan.product.productName')) === lowerCase(this._productName);
                    });
                }),
                tap((filteredData) => {
                    // Store in BehaviorSubject
                    this.companySubscriptionByCompanyId$.next({
                        companyId: companyId,
                        data: filteredData
                    });
                    // Mark as loaded
                    this.companySubscriptionLoadingState.set(cacheKey, false);
                })
            );
        },
        'getCompanySubscriptionUtilisationLevers': (companySubscriptionID)=>{
            const option = {
                url: `${this.apiURL}/company-subscription/utilisation-levers/${companySubscriptionID}`
            }
            return this.dataService.get(option)
        },
        'removeScheduledCancellationByChargebee': (companySubscriptionId) => {
            const option = {
                url: `${this.apiURL}/company-subscription-plan/${companySubscriptionId}/remove-scheduled-cancellation-by-chargebee`
            }
            return this.dataService.put(option);
        },
        'cancelRequest':(existingCompanySubscriptionId) => {
            const option = {
                url: `${this.apiURL}/company-subscription-plan/${existingCompanySubscriptionId}/cancel-request`
            }
            return this.dataService.put(option);
        },
        'downloadMetrics': (companyId, overagesCycles) => {
            const option = {
                url: `${this.apiURL}/subscription-metrics/download-metrics`,
                param: {
                    companyId
                },
                data: overagesCycles,
                responseType: 'blob' as 'json' | 'blob' | 'text' | 'arraybuffer'
            }

            return this.dataService.downloadFile(option)
        },
        'getCompanyOveragesCycle': (companyId, paginationMetaData?) => {
            const option = {
                url: `${this.apiURL}/company-subscription-stats/overages-cycle/${companyId}`,
                param: {
                    page: paginationMetaData?.page,
                    size: paginationMetaData?.size,
                    sortDirection: paginationMetaData?.sortDirection
                }
            }
            return this.dataService.get(option)
        },
        /**
         * Fetches current billing cycle usage metrics for a specific integration
         * @param companyId - The company identifier
         * @param integrationName - The name of the integration
         * @returns Observable with usageAggregateList containing current cycle data including:
         *   - totalRowsProcessed: Total rows processed in current cycle
         *   - overageCycleStartDate: Current cycle start timestamp
         *   - overageCycleEndDate: Current cycle end timestamp
         */
        'getCurrentUsageMetrics': (companyId: number, integrationName: string) => {
            const option = {
                url: `${this.apiURL}/subscription-metrics/usage/current`,
                param: {
                    companyId: companyId.toString(),
                    integrationName
                }
            }
            return this.dataService.get(option)
        },
        /**
         * Fetches historical usage data across multiple billing cycles
         * @param companyId - The company identifier
         * @param integrationName - The name of the integration
         * @param fromDate - Optional start date filter (timestamp in milliseconds)
         * @param toDate - Optional end date filter (timestamp in milliseconds)
         * @returns Observable with usageAggregateList containing cycle-level aggregates.
         *   Each cycle includes totalRowsProcessed and overageCycleStartDate
         * @note This returns billing cycle aggregates, not daily granular data
         */
        'getPastCyclesUsage': (companyId: number, integrationName: string, fromDate?: number, toDate?: number) => {
            const option = {
                url: `${this.apiURL}/subscription-metrics/usage/cycles`,
                param: {
                    companyId: companyId.toString(),
                    integrationName,
                    ...(fromDate && { fromDate: fromDate.toString() }),
                    ...(toDate && { toDate: toDate.toString() })
                }
            }
            return this.dataService.get(option)
        }
    }

    chargebee = {
        'estimateUpgradeByPlanName':  (activeCompanySubscriptionChargebeeSubscriptionId , newChargebeePlanId) => {
            const option = {
                url: `${this.apiURL}/chargebee/${activeCompanySubscriptionChargebeeSubscriptionId}/estimate-upgrade?newPlanId=${newChargebeePlanId}`,
                data: {}
            }
            return this.dataService.post(option);
        },
        'estimateDowngradeByPlanName':  (currentPlanName, newPlanName) => {
            const option = {
                url: `${this.apiURL}/chargebee/${currentPlanName}/estimate-downgrade?newPlanId=${newPlanName}&endOfTerm=true`,
                data: {}
            }
            return this.dataService.post(option);
        },
        'estimateCancellationByCurrentSubscriptionPlanId':  (currentActiveCompanySubscriptionPlanId: number) => {
            const option = {
                url: `${this.apiURL}/chargebee/${currentActiveCompanySubscriptionPlanId}/estimate-cancellation?endOfTerm=true`,
                data: {}
            }
            return this.dataService.post(option);
        },
        'getManagePaymentSourcesHostedPage': (chargebeeCustomerId) => {
            const option = {
                url: `${this.apiURL}/chargebee/manage-payment-sources/${chargebeeCustomerId}`
            }
            return this.dataService.get(option);
        },
        'getHostPageURL': (data) => {
            const option = {
                url: `${this.apiURL}/chargebee/redirect-host-page`,
                data: data
            }
            return this.dataService.post(option);
        },
        'getCustomerCardDetails': (chargebeeCustomerId) => {
            const option = {
                url: `${this.apiURL}/chargebee/customer-card-details?customerId=${chargebeeCustomerId}`
            }
            return this.dataService.get(option)
        },
        'getInvoicesByChargebeeCustomerId': (chargebeeCustomerId, fromDateInMilliSeconds = null, toDateInMilliSeconds = null, nextOffset = [] ) => {
            let param = {};
            if(!isNil(fromDateInMilliSeconds) && !isNil(toDateInMilliSeconds)){
                param['fromDate'] = fromDateInMilliSeconds;
                param['toDate']= toDateInMilliSeconds;
            }
            if(nextOffset.length > 0 ){
                param['offset1'] = nextOffset[0];
                param['offset2']= nextOffset[1];
            }
            const option:RequestParam = {
                url: `${this.apiURL}/chargebee/invoices?customerId=${chargebeeCustomerId}`,
                param : param
            }
            
            return this.dataService.get(option)
        },
        'getInvoicePdfUrl':(invoice) => {
            const option = {
                url: `${this.apiURL}/chargebee/invoicePdfUrl?invoiceIds=${invoice}`
            }
            return this.dataService.get(option)
        },
        'getAllBillingAddressByCompanyId': (companyId: number, subscriptionPlanId) => {
            const option = {
                url: `${this.apiURL}/chargebee/company/${companyId}/billing-address?subscriptionPlanId=${subscriptionPlanId}`
            }
            return this.dataService.get(option)
        },
        'updateBillingAddressByCompanyId': (companyId: number, subscriptionPlanId, billingAddress:UpdateBillingAddress) => {
            const option = {
                url: `${this.apiURL}/chargebee/company/${companyId}/billing-address/update?subscriptionPlanId=${subscriptionPlanId}`,
                data: billingAddress
            }
            return this.dataService.put(option)
        }
    }

    product = {
        'getProducts': () => {
            const option = {
                url: `${this.apiURL}/product`
            }
            return this.dataService.get(option)
        }
    }

}
